import { useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, Grid, createTheme } from '@mui/material';
import { DashboardContainer, DashboardHeader, colors } from './DashboardStyles';
import { StatCard } from './StatCard';

import { FocusPitchesCard } from './FocusPitchesCard';
import { QuickActionsCard } from './QuickActionsCard';
import i18n from '@/i18n'
import moment from 'moment'
import { useLoaderData, useSearchParams } from 'react-router-dom';
import { gql, useMutation } from '@apollo/client';
import client from '@/client'
import { CreatePitchForm } from '../pitch/create';
import { ReleasesSection } from './ReleasesSection';

const QUERY = gql`
query getPitchStats($start: Date, $end: Date, $source: String, $p: Int) {
  pitch {
    focusPitches: pitches(p: $p, source: "focus", start: $start, end: $end) {
      previousPageNumber
      nextPageNumber
      objects {
        id
        slug
        source
        streamUrl
        pitched
        handled
        release {
          name
          id
          slug
          label {
            id
            slug
            name
          }
          artists {
            name
            id
            slug
          }
          released
        }
        releaseSuggestions {
          release {
            id
            slug
            name
            artists {
              name
              id
              slug
            }
            label {
              id
              slug
              name
            }
          }
        }
        source
      }
    }
    pitches(p: $p, source: $source, start: $start, end: $end) {
      previousPageNumber
      nextPageNumber
      objects {
        id
        slug
        source
        streamUrl
        pitched
        handled
        release {
          name
          id
          slug
          label {
            id
            slug
            name
          }
          artists {
            name
            id
            slug
          }
          released
        }
        releaseSuggestions {
          release {
            id
            slug
            name
            artists {
              name
              id
              slug
            }
            label {
              id
              slug
              name
            }
          }
        }
        source
      }
    }
    stats {
      number
      label
      type
    }
  }
}`;

// Create MUI theme with custom fonts
const theme = createTheme({
  typography: {
    fontFamily: "'Plein', -apple-system, sans-serif",
  },
  palette: {
    background: {
      default: `linear-gradient(135deg, ${colors.nordicWhite} 0%, ${colors.iceBlue} 100%)`,
    },
  },
});

export function PitchDashboardPage() {
  const {
    focusPitches,
    pitches,
    p,
    stats
  } = useLoaderData();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeDateFilter = searchParams.get('period') || 'all';
  const activeSourceFilter = searchParams.get('source') || 'all';
 
  const handleDateFilterChange = (filter) => {
    setSearchParams((params) => {
      params.set('period', filter.id);
      return params;
    });
  }

  const handlePageChange = (p) => {
    setSearchParams((params) => {
      params.set('p', p);
      return params;
    });
  }

  const handleSourceFilterChange = (filter) => {
    setSearchParams((params) => {
      params.set('source', filter.id);
      return params;
    });
  }
  
  return (
    <PitchDashboard
      pitches={pitches}
      stats={stats}
      activeSourceFilter={activeSourceFilter}
      onPageChange={handlePageChange}
      p={p}
      onSourceFilterChange={handleSourceFilterChange}
      focusPitches={focusPitches}
      activeDateFilter={activeDateFilter}
      onDateFilterChange={handleDateFilterChange}
    />
  );
}

const SYNC_PITCHES_FROM_FUGA_MUTATION = gql`
  mutation syncPitchesFromFuga($organizationId: ID, $labelId: ID) {
    syncPitchesFromFuga(
      organizationId: $organizationId,
      labelId: $labelId
    ) {
      ok
    }
  }
`

export function PitchDashboard({ p, onPageChange, activeDateFilter, pitches, focusPitches, stats, onDateFilterChange }) {
  const [syncPitchesFromFuga, { loading: syncing }] = useMutation(SYNC_PITCHES_FROM_FUGA_MUTATION)
  const [releaseId, setReleaseId] = useState(null)
  const [createPitchDialogOpen, setCreatePitchDialogOpen] = useState(false)
  const setActiveDateFilter = (dateFilterId) => {
    onDateFilterChange(dateFilterId);
  }
  const handleDateFilterChange = (filter) => {
    setActiveDateFilter(filter);
    // Add filter logic here to filter releases based on selected filter
  };

  const handleSubmitPitch = () => {
    console.log('Submit new pitch clicked');
    setReleaseId(null)
    setCreatePitchDialogOpen(true)
  };

  const handleExportReport = () => {
    console.log('Export weekly report clicked');
    // Add export logic here
  };

  const handleSyncFuga = () => {
    console.log('Sync with FUGA clicked');
    syncPitchesFromFuga({
      variables: {}
    }).then(() => {

    }).catch(e => {
      console.error(e)
    })
  };

  return (
    <Box
      sx={{
        minHeight: '100vh'
      }}
    >
      <DashboardContainer>
        <DashboardHeader>
          <h1>Pitch Dashboard</h1>
          <p>Spotify submissions & release pipeline for Aloaded</p>
        </DashboardHeader>
        {/* Stats Grid */}
        <Grid container spacing={2} sx={{ marginBottom: 4 }}>
          {stats?.length > 0 ? stats?.map((stat, index) => (
            <Grid item xs={12} sm={ 6} md={Math.floor(12 / stats.length)} key={index}>
              <StatCard number={stat.number} label={i18n.t(stat.label)} />
            </Grid>
          )) : (
            <Grid item xs={12} sm={6} md={3}>
              <StatCard number={0} label="No stats found" />
            </Grid>
          )}
        </Grid>
        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Releases Section */}
          <Grid item xs={12} lg={8}>
            <ReleasesSection
              p={p ?? 1}
              onPageChange={onPageChange}
              pitches={pitches?.objects ?? []}
              activeDateFilter={activeDateFilter}
              onDateFilterChange={handleDateFilterChange}
            />
          </Grid>
          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Focus Releases */}
              <FocusPitchesCard
                pitches={focusPitches?.objects ?? []}
                focusCount={focusPitches?.objects?.length}
              />
              {/* Quick Actions */}
              <QuickActionsCard
                onSubmitPitch={handleSubmitPitch}
                onExportReport={handleExportReport}
                onSyncFuga={handleSyncFuga}
                syncingWithFuga={syncing}
              />
            </Box>
          </Grid>
        </Grid>
      </DashboardContainer>
      <Dialog fullWidth open={createPitchDialogOpen} onClose={() => setCreatePitchDialogOpen(false)}>
        <DialogTitle>{i18n.t('create-pitch')}</DialogTitle>
        <DialogContent>
          <CreatePitchForm onClose={() => { setCreatePitchDialogOpen(false); }} releaseId={releaseId} onCreated={() => { refetch(); }} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function getDate(date, defaultDate = null) {
  return moment(date).isValid() ? moment(date).format('YYYY-MM-DD') : defaultDate;
}

PitchDashboardPage.loader = async ({ request }) => {
  const url = new URL(request.url)
  const q = url.searchParams.get('q');
  let start = getDate(url.searchParams.get('start'), moment());
  let end = getDate(url.searchParams.get('end'), moment().add(1, 'month'));
  let p = parseInt(url.searchParams.get('p') ?? 1)

  let source = ''
  if (url.searchParams.has('source')) {
    source = url.searchParams.get('source')
  }
  if (url.searchParams.has('period')) {
    const period = url.searchParams.get('period');
    switch (period) {
      case 'all':
        start = moment().startOf('week');
        end = moment().add(1, 'month')
        break;
      case 'this-week':
        start = moment().startOf('week');
        end = moment().endOf('week');
        break;
      case 'next-week':
        start = moment().add(1, 'week').startOf('week');
        end = moment().add(1, 'week').endOf('week');
        break;
      case 'this-month':
        start = moment().startOf('month');
        end = moment().endOf('month');
        break;
      case 'next-month':
        start = moment().add(1, 'month').startOf('month');
        end = moment().add(1, 'month').endOf('month');
        break;
    }
  }
  start = start.format('YYYY-MM-DD')
  end = end.format('YYYY-MM-DD')

  const { data } = await client.query({
    query: QUERY,
    variables: {
      q,
      p,
      start,
      source,
      end
    }
  });

  const { focusPitches, pitches } = data.pitch;

  if (pitches?.length < 1) {
    console.warn('No releases found')
  }
  if (focusPitches?.objects?.length < 1) {
    console.warn('No focus pitches found')
  }

  const stats = data.pitch.stats;

  return {
    p,
    pitches,
    focusPitches,
    pitches,
    source,
    stats
  };
};


export default PitchDashboardPage;
